import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import logoIcon from '../images/optycargo-icon.png';

export default function LoginPage() {
  const { user, login, loading, slug } = useAuth();
  const navigate = useNavigate();
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState(null);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await login(correo, contrasena);
      navigate('/', { replace: true });
    } catch (e2) {
      setErr(e2.message || 'No fue posible iniciar sesión');
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Fondo full-bleed con gradiente y círculos desenfocados */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-indigo-50 to-slate-50 dark:from-slate-900 dark:via-indigo-900 dark:to-slate-800" />
        <div className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-indigo-400/20 dark:bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-fuchsia-400/10 dark:bg-fuchsia-500/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.03)_1px,_transparent_1px)] dark:bg-[radial-gradient(transparent_1px,_rgba(255,255,255,0.04)_1px)] [background-size:16px_16px]" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md shadow-2xl border bg-white text-slate-900 dark:border-0 dark:backdrop-blur dark:supports-[backdrop-filter]:bg-white/10 dark:bg-white/10 dark:text-white">
          <CardHeader className="space-y-3 items-center text-center">
            <div className="flex flex-col items-center gap-4">
              <img
                src={logoIcon}
                alt="OptyCargo"
                className="h-32 w-32 object-contain"
              />
              <h1 className="text-2xl font-bold text-foreground">OptyCargo</h1>
              <CardTitle className="text-base font-semibold gradient-text">
                Iniciar sesión
              </CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-200/80">
                {slug ? (
                  <>
                    Operador <span className="font-semibold">{slug}</span>
                  </>
                ) : (
                  'Plataforma matriz'
                )}
              </CardDescription>
            </div>
          </CardHeader>
          <form onSubmit={onSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="correo">Correo electrónico</Label>
                <Input
                  id="correo"
                  type="email"
                  placeholder="usuario@empresa.com"
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  required
                  autoFocus
                  className="bg-white text-slate-900 placeholder:text-slate-400 dark:bg-white/10 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contrasena">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="contrasena"
                    type={showPassword ? 'text' : 'password'}
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    required
                    className="pr-12 bg-white text-slate-900 placeholder:text-slate-400 dark:bg-white/10 dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-1.5 my-auto h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-white/80 dark:hover:text-white dark:hover:bg-white/10 transition"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {err && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                  {err}
                </div>
              )}
            </CardContent>
            <div className="px-6 pb-6 flex flex-col space-y-3">
              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
                disabled={loading}
              >
                {loading ? 'Iniciando sesión…' : 'Iniciar sesión'}
              </Button>
              <p className="text-xs text-center text-slate-600 dark:text-slate-200/80">
                ¿No tienes cuenta? <span className="text-indigo-600 dark:text-indigo-200">Contacta al administrador</span>
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
